import React, { useEffect, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { BiMessageSquareAdd } from "react-icons/bi";
import ClipLoader from "react-spinners/ClipLoader";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { FaCaretDown } from "react-icons/fa";
import { RiErrorWarningFill } from "react-icons/ri";
import useAuthAxios from "../../utils/baseAxios";
import { API } from "../../constants/api";
import { errorMessage } from "../../utils/errorMessage";
import { useUser } from "../../context/user-context";
import useUserAsset from "../../hooks/useUserAssets";
import { formatAmount } from "../../utils/formatTime";
import PortionAmount from "../../components/PortionAmount";
import nairaLogo from "../../assets/images/Naira.svg";
import { NAIRA_WITHDRAWAL_FEE } from "../../constants/constants";
import NairaWithdrawPreview from "./withdraw/NairaWithdrawPreview";
import NairaWithdrawSuccess from "./withdraw/NairaWithdrawSuccess";

type NairaWithdrawModalProps = {
  setOpenWithdraw: (open: boolean) => void;
};

const NairaWithdrawModal: React.FC<NairaWithdrawModalProps> = ({
  setOpenWithdraw,
}) => {
  const axiosInstance = useAuthAxios();
  const navigate = useNavigate();
  const { userDetails, refetch } = useUser();
  const { walletBalance } = useUserAsset();
  const ngnBalance = walletBalance?.ngn ?? 0;
  // The API takes whole naira and debits amount + fee, so this is the most a user can send.
  const maxWithdrawable = Math.max(
    0,
    Math.floor(ngnBalance - NAIRA_WITHDRAWAL_FEE)
  );

  const [ngnAmount, setNgnAmount] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [accountName, setAccountName] = useState("");
  const [bankId, setBankId] = useState("");
  const [openBank, setOpenBank] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);
  const [openWithdrawPreview, setOpenWithdrawPreview] = useState(false);
  const [openWithdrawSuccess, setOpenWithdrawSuccess] = useState(false);

  // Whole naira only — the withdraw endpoint's `amount` is an integer.
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNgnAmount(e.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, ""));
  };

  const getAllBanks = async () => {
    const response = await axiosInstance.get(API.getBanks);
    return response.data;
  };

  const getAllOldBanks = async () => {
    const response = await axiosInstance.get(API.getBanksDeex);
    return response.data;
  };

  const { data: allBanks, error: banksError } = useQuery({
    queryKey: ["all-banks-naira-withdraw"],
    queryFn: getAllBanks,
    retry: 1,
  });

  const { data: allOldBanks, error: oldBanksError } = useQuery({
    queryKey: ["all-old-banks-naira-withdraw"],
    queryFn: getAllOldBanks,
    retry: 1,
  });

  useEffect(() => {
    if (banksError) {
      const err = banksError as { message?: string; data?: { message?: string } };
      toast.error(errorMessage(err?.message || err?.data?.message));
    }
    if (oldBanksError) {
      const err = oldBanksError as { message?: string; data?: { message?: string } };
      toast.error(errorMessage(err?.message || err?.data?.message));
    }
  }, [banksError, oldBanksError]);

  const resolveBankName = (bank: {
    bankCode: string;
    bankName?: string;
  }) => {
    const matchedBank =
      allBanks?.data?.find(
        (allBank: { bankCode: string }) => allBank.bankCode === bank.bankCode
      ) ||
      allOldBanks?.data?.find(
        (newBank: { code: string }) => newBank.code === bank.bankCode
      );

    return (
      matchedBank?.bankName ||
      matchedBank?.name ||
      bank.bankName ||
      "Unknown Bank"
    );
  };

  useEffect(() => {
    if (
      userDetails?.user?.banks?.length > 0 &&
      allBanks?.data &&
      allOldBanks?.data
    ) {
      const defaultBank =
        userDetails.user.banks.find((bank: { isDefault: boolean }) => bank.isDefault) ||
        userDetails.user.banks[0];

      setAccountNumber(defaultBank.accountNumber);
      setBankCode(defaultBank.bankCode);
      setBankName(resolveBankName(defaultBank));
      setAccountName(defaultBank.accountName);
    }
  }, [userDetails?.user?.banks, allBanks?.data, allOldBanks?.data]);

  const makeDefault = async ({
    accountNumber,
    bankCode: code,
  }: {
    accountNumber: string;
    bankCode: string;
  }) => {
    const response = await axiosInstance.put(API.makeDefaultBank(bankId), {
      accountNumber,
      bankCode: code,
    });
    return response.data;
  };

  const completeMakeDefault = useMutation({
    mutationFn: makeDefault,
    onSuccess: (response) => {
      toast.success(response.message);
      refetch();
      setLoadingIndex(null);
      setTimeout(() => setOpenBank(false), 1000);
    },
    onError: (err) => {
      const e = err as { message?: string; data?: { message?: string } };
      toast.error(errorMessage(e?.message || e?.data?.message));
      setLoadingIndex(null);
    },
  });

  const totalDebit = Number(ngnAmount) + NAIRA_WITHDRAWAL_FEE;

  const errorMessageText = (() => {
    if (ngnAmount !== "" && Number(ngnAmount) > maxWithdrawable) {
      return `Insufficient balance. You need ₦${formatAmount(
        totalDebit
      )} (amount + ₦${NAIRA_WITHDRAWAL_FEE} fee)`;
    }
    return "";
  })();

  useEffect(() => {
    if (
      !accountNumber ||
      !bankCode ||
      !accountName ||
      Number(ngnAmount) <= 0 ||
      Number(ngnAmount) > maxWithdrawable
    ) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [ngnAmount, accountNumber, bankCode, accountName, maxWithdrawable]);

  return (
    <div className="fixed inset-0 z-[130] flex items-start justify-start overflow-auto bg-[#E7EBEE] pb-16 pt-16 font-manrope dark:bg-primary_dark dark:bg-opacity-95 backdrop-blur-sm">
      <div className="mx-auto w-11/12 rounded-xl border bg-white p-6 dark:border-[#303030] dark:bg-[#192025] mds:w-8/12 lgss:w-2/5 xxl:w-1/3">
        <div className="flex w-full items-center justify-start gap-6">
          <button type="button" onClick={() => setOpenWithdraw(false)}>
            <BsArrowLeft className="text-[16px] text-gray-800 dark:text-gray-200" />
          </button>
          <h4 className="font-sora text-[20px] font-semibold text-gray-900 dark:text-gray-100">
            Withdraw
          </h4>
        </div>

        <div className="mt-2 w-full">
          <div className="mt-6">
            <h4 className="text-[14px] font-bold text-gray-500">Amount</h4>

            <div className="mt-3 flex w-full items-start justify-between rounded-[4px] border border-[#EAEAEA] p-3 shadow dark:border-gray-700">
              <div className="w-[50%]">
                <div className="relative flex w-full items-center justify-start gap-2">
                  <div className="flex h-[22px] w-[22px] items-center justify-center rounded-full">
                    <img
                      src={nairaLogo}
                      className="h-full w-full bg-cover"
                      alt=""
                    />
                  </div>
                  <div className="w-[60%]">
                    <p className="text-[14px] font-semibold text-gray-900 dark:text-gray-200">
                      Nigerian Naira
                    </p>
                  </div>
                </div>
                <h4 className="mt-2 pl-2 text-[12px] font-medium text-gray-500">
                  NGN
                </h4>
              </div>
              <div className="w-[40%]">
                <div className="flex w-full items-center justify-end gap-1 text-right text-gray-900 dark:text-gray-200">
                  <span className="font-sora text-[18px] font-bold">₦</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="0"
                    value={ngnAmount}
                    autoFocus
                    onChange={handleAmountChange}
                    style={{ width: `${Math.max(ngnAmount.length, 4)}ch` }}
                    className="spin-button-none bg-transparent text-right text-[18px] font-semibold outline-none"
                  />
                </div>
              </div>
            </div>

            {errorMessageText ? (
              <p className="mt-1 text-[12px] font-semibold italic text-red-500">
                {errorMessageText}
              </p>
            ) : (
              <p className="mt-1 text-[12px] font-medium text-gray-500 dark:text-gray-400">
                ₦{NAIRA_WITHDRAWAL_FEE} transfer fee applies
                {Number(ngnAmount) > 0 &&
                  ` · Total debit ₦${formatAmount(totalDebit)}`}
              </p>
            )}
          </div>

          <div className="mt-2 flex items-center justify-between font-semibold">
            <h4 className="text-center text-[11px] text-[#BE6B0A]">
              Bal: <span>₦{formatAmount(ngnBalance)}</span>
            </h4>
            <PortionAmount
              setAmount={setNgnAmount}
              coinBalance={maxWithdrawable}
              onPortionSelect={(_portion: number, rawCrypto: number) => {
                const whole = Math.floor(rawCrypto);
                setNgnAmount(whole > 0 ? String(whole) : "");
              }}
            />
          </div>

          <div className="relative mt-8 w-full">
            <h4 className="text-[14px] font-bold text-gray-500">Destination</h4>
            <div
              onClick={() => setOpenBank((prev) => !prev)}
              className="mt-3 flex w-full cursor-pointer items-center justify-between rounded-[4px] border border-[#EAEAEA] p-3 shadow dark:border-gray-700"
            >
              <div className="flex items-center justify-start gap-3">
                <div>
                  <h4 className="text-[13px] font-semibold text-black dark:text-gray-50">
                    {accountNumber ? `${accountNumber} - ${bankName}` : "Select bank account"}
                  </h4>
                  {accountName && (
                    <h4 className="mt-1 text-[11px] font-medium text-gray-500 dark:text-gray-50">
                      {accountName}
                    </h4>
                  )}
                </div>
              </div>
              <FaCaretDown className="text-[20px] text-gray-700 dark:text-gray-300" />
            </div>

            {openBank && (
              <div className="absolute z-50 mt-2 h-[250px] w-full overflow-auto rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-600 dark:bg-gray-800">
                {userDetails?.user?.banks?.length >= 1 ? (
                  <div className="w-full">
                    {userDetails.user.banks
                      .sort(
                        (a: { isDefault: boolean }, b: { isDefault: boolean }) =>
                          Number(b.isDefault) - Number(a.isDefault)
                      )
                      .map(
                        (
                          bank: {
                            _id: string;
                            isDefault: boolean;
                            accountNumber: string;
                            accountName: string;
                            bankCode: string;
                          },
                          index: number
                        ) => {
                          const matchedBank =
                            allBanks?.data?.find(
                              (allBank: { bankCode: string }) =>
                                allBank.bankCode === bank.bankCode
                            ) ||
                            allOldBanks?.data?.find(
                              (newBank: { code: string }) =>
                                newBank.code === bank.bankCode
                            );
                          const resolvedName =
                            matchedBank?.bankName ||
                            matchedBank?.name ||
                            "Unknown Bank";

                          return (
                            <div
                              key={bank._id}
                              onClick={() => {
                                if (bank.isDefault) {
                                  setAccountNumber(bank.accountNumber);
                                  setBankCode(bank.bankCode);
                                  setBankName(resolvedName);
                                  setAccountName(bank.accountName);
                                  setOpenBank(false);
                                } else {
                                  setBankId(bank._id);
                                  setLoadingIndex(index);
                                  setTimeout(() => {
                                    completeMakeDefault.mutate({
                                      accountNumber: bank.accountNumber,
                                      bankCode: bank.bankCode,
                                    });
                                    setAccountNumber(bank.accountNumber);
                                    setBankCode(bank.bankCode);
                                    setBankName(resolvedName);
                                    setAccountName(bank.accountName);
                                  }, 500);
                                }
                              }}
                              className={`flex w-full cursor-pointer items-center gap-4 p-4 ${
                                bank.isDefault
                                  ? "rounded-xl border border-secondary_button"
                                  : "border-b border-gray-200 dark:border-gray-600"
                              }`}
                            >
                              {loadingIndex === index ? (
                                <ClipLoader color="#5E91FF" size={20} />
                              ) : (
                                <div
                                  className={`flex h-[20px] w-[20px] items-center justify-center rounded-full border p-1 ${
                                    bank.isDefault
                                      ? "border-secondary_button"
                                      : "border-[#505050] bg-transparent"
                                  }`}
                                >
                                  <div
                                    className={`h-full w-full rounded-full ${
                                      bank.isDefault
                                        ? "bg-secondary_button"
                                        : "bg-transparent"
                                    }`}
                                  />
                                </div>
                              )}
                              <div>
                                <h4 className="text-[10px] font-bold text-gray-700 dark:text-gray-200">
                                  {bank.accountName}
                                </h4>
                                <h4 className="text-[14px] text-gray-900 dark:text-gray-100">
                                  {bank.accountNumber} - {resolvedName}
                                </h4>
                              </div>
                            </div>
                          );
                        }
                      )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate("/settings")}
                    className="mt-8 flex w-full items-center justify-center gap-4 rounded-xl bg-secondary_button p-3 text-[14px] text-white transition duration-300 ease-out mds:mt-0"
                  >
                    <span>Add bank</span>
                    <BiMessageSquareAdd className="text-2xl" />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 flex items-start gap-4 rounded-lg bg-[#E8F3FC] px-4 py-3 dark:bg-[#9AD1F9]">
            <RiErrorWarningFill className="text-[30px] text-secondary_button" />
            <h4 className="mt-1 text-[13px]">
              Payout above <span className="font-bold">5,000,000</span> Naira
              will be paid in batches.
            </h4>
          </div>

          <button
            type="button"
            onClick={() => setOpenWithdrawPreview(true)}
            disabled={disabled}
            className={
              disabled
                ? "mt-8 flex h-[48px] w-full items-center justify-center rounded-xl bg-gray-400 text-[15px] font-medium text-gray-200"
                : "mt-8 flex h-[48px] w-full items-center justify-center rounded-xl bg-secondary_button text-[15px] font-medium text-white"
            }
          >
            Done
          </button>
        </div>
      </div>

      {openWithdrawPreview && (
        <NairaWithdrawPreview
          setOpenWithdraw={setOpenWithdraw}
          setOpenWithdrawPreview={setOpenWithdrawPreview}
          setOpenWithdrawSuccess={setOpenWithdrawSuccess}
          amount={ngnAmount}
          accountNumber={accountNumber}
          bankName={bankName}
          accountName={accountName}
        />
      )}

      {openWithdrawSuccess && (
        <NairaWithdrawSuccess
          setOpenWithdrawSuccess={setOpenWithdrawSuccess}
          setOpenWithdraw={setOpenWithdraw}
          amount={ngnAmount}
        />
      )}
    </div>
  );
};

export default NairaWithdrawModal;
